declare global {
  interface Window {
    googleTranslateElementInit?: () => void
  }
}

let googleTranslateReady = false

function getParentDomain(): string | null {
  const host = window.location.hostname
  const parts = host.split('.')
  if (parts.length > 2) {
    return '.' + parts.slice(parts.length - 2).join('.')
  }
  return null
}

export function clearParentDomainCookie(): void {
  const parent = getParentDomain()
  if (parent) {
    document.cookie = 'googtrans=;path=/;domain=' + parent + ';expires=Thu, 01 Jan 1970 00:00:00 UTC'
  }
}

export function isSpanishSelected(): boolean {
  const cookies = document.cookie.split(';')
  for (let i = 0; i < cookies.length; i++) {
    const c = cookies[i].trim()
    if (c.indexOf('googtrans=') === 0 && c.indexOf('/en/es') !== -1) {
      return true
    }
  }
  return false
}

/** Angular `translateContent`: only auto-translate when the page is not English. */
export function shouldAutoTranslateFieldContent(): boolean {
  if (isSpanishSelected()) return true

  const parent = document.querySelector('#app-parent-container') as HTMLElement | null
  const lang = (parent?.lang || document.documentElement.lang || 'en').trim().toLowerCase()
  return lang !== '' && lang !== 'en'
}

/** Re-trigger Google Translate after SPA navigation (combo is created by TranslateElement). */
export function refreshGoogleTranslate(): void {
  if (!isSpanishSelected()) return

  const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo')
  if (!combo) return

  const trigger = () => combo.dispatchEvent(new Event('change', { bubbles: true }))
  const hasEnglishOption = Array.from(combo.options).some(
    (option) => option.value === '' || option.value === 'en',
  )

  if (combo.value === 'es') {
    // Already Spanish — toggle back to English then Spanish so Google re-scans new DOM
    // (dispatching `change` while still on `es` does not call translateHtml again).
    combo.value = hasEnglishOption ? '' : 'en'
    trigger()
    window.setTimeout(() => {
      combo.value = 'es'
      trigger()
    }, 50)
    return
  }

  combo.value = 'es'
  trigger()
}

/** Wait for TranslateElement, then re-apply Spanish after React paints new tab content. */
export function refreshGoogleTranslateWhenReady(): void {
  if (!isSpanishSelected()) return

  ensureGoogleTranslateLoaded(() => {
    window.requestAnimationFrame(() => {
      window.setTimeout(() => refreshGoogleTranslate(), 150)
    })
  })
}

function waitForGoogleTranslateCombo(callback: () => void): void {
  const poll = window.setInterval(() => {
    const combo = document.querySelector('.goog-te-combo')
    if (!combo) return
    window.clearInterval(poll)
    googleTranslateReady = true
    window.setTimeout(clearParentDomainCookie, 500)
    callback()
  }, 200)
}

export function ensureGoogleTranslateLoaded(callback: () => void): void {
  if (googleTranslateReady) {
    callback()
    return
  }

  window.googleTranslateElementInit = function () {
    const g = window as unknown as Record<string, unknown>
    const translate = g['google'] && (g['google'] as Record<string, unknown>)['translate']
    const TranslateElement = translate && (translate as Record<string, unknown>)['TranslateElement']
    if (typeof TranslateElement !== 'function') {
      return
    }

    const translateElement = document.getElementById('google_translate_element')
    if (translateElement && !translateElement.hasChildNodes()) {
      new (TranslateElement as new (...args: unknown[]) => unknown)(
        { pageLanguage: 'en', includedLanguages: 'en,es', autoDisplay: false },
        'google_translate_element',
      )
    }

    waitForGoogleTranslateCombo(callback)
  }

  const existingScript = document.querySelector('script[data-google-translate="true"]')
  if (existingScript) {
    if (document.querySelector('.goog-te-combo')) {
      googleTranslateReady = true
      callback()
      return
    }
    waitForGoogleTranslateCombo(callback)
    return
  }

  const s = document.createElement('script')
  s.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
  s.setAttribute('data-google-translate', 'true')
  document.body.appendChild(s)
}

export function changeLanguage(lang: string): void {
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC'
  const host = window.location.hostname
  const parent = getParentDomain()

  document.cookie = 'googtrans=;path=/;' + expired
  document.cookie = 'googtrans=;path=/;domain=' + host + ';' + expired
  if (parent) {
    document.cookie = 'googtrans=;path=/;domain=' + parent + ';' + expired
  }

  if (lang === 'es') {
    document.cookie = 'googtrans=/en/es;path=/'
    document.cookie = 'googtrans=/en/es;path=/;domain=' + host
  }

  location.reload()
}
